import type { AxiosInstance } from "axios";
import { internalApiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  CampaignTrackerDetailResponse,
  CampaignTrackerGroupFields,
  CampaignTrackerHistoryResponse,
  CampaignTrackerListResponse,
  CampaignTrackerMembership,
  CampaignTrackerMetadata,
  CampaignTrackerMutationResponse,
} from "@/marketing/models/campaignTracker";

export interface TrackerMutationContext {
  expectedVersion: number;
  clientReportedActor?: string;
}
export interface TrackerMetadataRequest extends TrackerMutationContext {
  patch: CampaignTrackerMetadata;
}
export interface TrackerGroupRequest extends TrackerMutationContext {
  patch: CampaignTrackerGroupFields;
}
export interface TrackerCreateGroupRequest extends TrackerMutationContext {
  group: CampaignTrackerGroupFields & { name: string };
}
export interface TrackerMembershipRequest extends TrackerMutationContext {
  assignments: CampaignTrackerMembership[];
}

const TRACKER_CHANGED_MESSAGE = "The tracker changed while loading. Refresh to load all groups.";

export class CampaignTrackerAPIClient {
  constructor(private readonly client: AxiosInstance = internalApiClient) {}

  public async list(): Promise<CampaignTrackerListResponse> {
    const limit = 200;
    const first = (await this.client.get<CampaignTrackerListResponse>("/influencer/campaignTracker", { params: { limit, offset: 0 } }))
      .data;
    const rows: CampaignTrackerListResponse["rows"] = [];
    const rowIds = new Set<string>();
    const campaignVersions = new Map<string, number>();
    // Offset pages are separate snapshots, not one atomic list. Reject observable drift.
    const appendPage = (page: CampaignTrackerListResponse) => {
      if (page.total !== first.total) throw new Error(TRACKER_CHANGED_MESSAGE);
      for (const row of page.rows) {
        const version = campaignVersions.get(row.campaignId);
        if (rowIds.has(row.rowId) || (version !== undefined && version !== row.version)) throw new Error(TRACKER_CHANGED_MESSAGE);
        rowIds.add(row.rowId);
        campaignVersions.set(row.campaignId, row.version);
        rows.push(row);
      }
    };
    appendPage(first);
    for (let offset = rows.length; offset < first.total; ) {
      const next = (await this.client.get<CampaignTrackerListResponse>("/influencer/campaignTracker", { params: { limit, offset } })).data;
      if (!next.rows.length) throw new Error(TRACKER_CHANGED_MESSAGE);
      appendPage(next);
      offset += next.rows.length;
    }
    if (rowIds.size !== first.total) throw new Error(TRACKER_CHANGED_MESSAGE);
    return { ...first, rows };
  }

  public async detail(campaignId: string): Promise<CampaignTrackerDetailResponse> {
    return (await this.client.get<CampaignTrackerDetailResponse>(this.path(campaignId))).data;
  }

  public async updateMetadata(campaignId: string, request: TrackerMetadataRequest): Promise<CampaignTrackerMutationResponse> {
    return (await this.client.patch<CampaignTrackerMutationResponse>(this.path(campaignId), request)).data;
  }

  public async createGroup(campaignId: string, request: TrackerCreateGroupRequest): Promise<CampaignTrackerMutationResponse> {
    return (await this.client.post<CampaignTrackerMutationResponse>(`${this.path(campaignId)}/groups`, request)).data;
  }

  public async updateGroup(campaignId: string, groupId: string, request: TrackerGroupRequest): Promise<CampaignTrackerMutationResponse> {
    return (
      await this.client.patch<CampaignTrackerMutationResponse>(`${this.path(campaignId)}/groups/${encodeURIComponent(groupId)}`, request)
    ).data;
  }

  public async setMemberships(campaignId: string, request: TrackerMembershipRequest): Promise<CampaignTrackerMutationResponse> {
    return (await this.client.put<CampaignTrackerMutationResponse>(`${this.path(campaignId)}/memberships`, request)).data;
  }

  public async history(campaignId: string, offset = 0): Promise<CampaignTrackerHistoryResponse> {
    return (await this.client.get<CampaignTrackerHistoryResponse>(`${this.path(campaignId)}/history`, { params: { limit: 50, offset } }))
      .data;
  }

  private path(campaignId: string): string {
    return `/influencer/campaigns/${encodeURIComponent(campaignId)}/tracker`;
  }
}

export const campaignTrackerAPIClient = new CampaignTrackerAPIClient();
